import { useState } from 'react';
import {
  Button,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { JURISDICTION_LIST } from '@/lib/constants';
import { useAccountsList } from '@/models/accounts';
import {
  TCorporateDetails,
  TIndividualDetails,
  TPayer,
  useCreatePayer,
} from '@/models/payers';

const COUNTRY_OPTIONS = JURISDICTION_LIST.map(({ code, title }) => ({
  value: code,
  label: title,
}));

type FormValues = {
  clientType: 'Individual' | 'Corporate';
  countryCode: string;
  fullName: string;
  documentNumber: string;
  nationality: string;
  legalAddress: string;
  companyName: string;
  companyRegNumber: string;
  companyTaxId: string;
  companyLegalAddress: string;
  businessDescription: string;
  companyWebsite: string;
};

export default function PayerCreateForm({
  accountId,
  onCreated,
  onCancel,
}: {
  accountId?: number | null;
  onCreated: (payer: TPayer) => void;
  onCancel: () => void;
}) {
  const createPayer = useCreatePayer();
  const needsAccount = !accountId;
  const [clientType, setClientType] = useState<'Individual' | 'Corporate'>(
    'Individual',
  );
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const { data: accounts } = useAccountsList({
    search: accountSearch || undefined,
    view: 'all',
    pageSize: 50,
  });

  const form = useForm<FormValues>({
    mode: 'uncontrolled',
    initialValues: {
      clientType: 'Individual',
      countryCode: '',
      fullName: '',
      documentNumber: '',
      nationality: '',
      legalAddress: '',
      companyName: '',
      companyRegNumber: '',
      companyTaxId: '',
      companyLegalAddress: '',
      businessDescription: '',
      companyWebsite: '',
    },
    validate: {
      countryCode: (v) => (!v ? 'Country is required' : null),
      fullName: (v, values) =>
        values.clientType === 'Individual' && !v.trim()
          ? 'Full name is required'
          : null,
      companyName: (v, values) =>
        values.clientType === 'Corporate' && !v.trim()
          ? 'Company name is required'
          : null,
      companyRegNumber: (v, values) =>
        values.clientType === 'Corporate' && !v.trim()
          ? 'Registration number is required'
          : null,
      companyLegalAddress: (v, values) =>
        values.clientType === 'Corporate' && !v.trim()
          ? 'Registered address is required'
          : null,
    },
  });

  const submit = form.onSubmit((values) => {
    const finalAccountId = accountId ?? Number(selectedAccountId);
    if (!finalAccountId) {
      notifications.show({
        color: 'orange',
        message: 'Select an account first.',
      });
      return;
    }
    const clientDetails: TIndividualDetails | TCorporateDetails =
      values.clientType === 'Individual'
        ? {
            fullName: values.fullName.trim(),
            documentNumber: values.documentNumber.trim(),
            nationality: values.nationality.trim(),
            residenceCountry: values.countryCode,
            legalAddress: values.legalAddress.trim(),
          }
        : {
            companyName: values.companyName.trim(),
            companyRegNumber: values.companyRegNumber.trim(),
            companyTaxId: values.companyTaxId.trim(),
            companyCountry: values.countryCode,
            companyLegalAddress: values.companyLegalAddress.trim(),
            businessDescription: values.businessDescription.trim(),
            companyWebsite: values.companyWebsite.trim(),
          };
    createPayer.mutate(
      {
        accountId: finalAccountId,
        clientType: values.clientType,
        countryCode: values.countryCode,
        clientDetails,
      },
      {
        onSuccess: (payer) => onCreated(payer),
        onError: (err) =>
          notifications.show({ color: 'red', message: String(err) }),
      },
    );
  });

  return (
    <form onSubmit={submit}>
      <Stack gap="sm">
        {needsAccount && (
          <Select
            label="Account"
            placeholder="Search account..."
            searchable
            required
            data={(accounts?.rows ?? []).map((a) => ({
              value: String(a.id),
              label: a.name,
            }))}
            searchValue={accountSearch}
            onSearchChange={setAccountSearch}
            value={selectedAccountId}
            onChange={setSelectedAccountId}
          />
        )}
        <SegmentedControl
          data={['Individual', 'Corporate']}
          value={clientType}
          onChange={(v) => {
            const type = v as 'Individual' | 'Corporate';
            setClientType(type);
            form.setFieldValue('clientType', type);
          }}
        />
        <Select
          label="Country"
          placeholder="Select country"
          searchable
          required
          data={COUNTRY_OPTIONS}
          key={form.key('countryCode')}
          {...form.getInputProps('countryCode')}
        />
        {clientType === 'Individual' ? (
          <>
            <TextInput
              required
              label="Full Name"
              key={form.key('fullName')}
              {...form.getInputProps('fullName')}
            />
            <Text size="xs" c="dimmed">
              Document number, nationality and legal address are collected
              automatically when the payer passes KYC.
            </Text>
          </>
        ) : (
          <>
            <TextInput
              required
              label="Legal Company Name"
              key={form.key('companyName')}
              {...form.getInputProps('companyName')}
            />
            <Group grow>
              <TextInput
                required
                label="Registration Number"
                key={form.key('companyRegNumber')}
                {...form.getInputProps('companyRegNumber')}
              />
              <TextInput
                label="Tax ID (TIN / VAT / GST)"
                key={form.key('companyTaxId')}
                {...form.getInputProps('companyTaxId')}
              />
            </Group>
            <Textarea
              required
              label="Registered Business Address"
              description="Street, House Number, Flat Number, ZIP Code, Country"
              autosize
              minRows={1}
              maxRows={4}
              key={form.key('companyLegalAddress')}
              {...form.getInputProps('companyLegalAddress')}
            />
            <Textarea
              label="Brief Description of Business Activities"
              autosize
              minRows={2}
              maxRows={4}
              key={form.key('businessDescription')}
              {...form.getInputProps('businessDescription')}
            />
            <TextInput
              label="Company Website URL"
              placeholder="https://example.com"
              key={form.key('companyWebsite')}
              {...form.getInputProps('companyWebsite')}
            />
          </>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onCancel}>
            Back
          </Button>
          <Button type="submit" loading={createPayer.isPending}>
            Create Payer
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
